import { BALANCE, COLS, MAP_W, colOf, inBounds, xOf, yOf } from '../constants';
import { FLAG_BUILDING, FLAG_CABLE, FLAG_PAVED, FLAG_PIPE, FLAG_ROAD, type Terrain } from '../types';
import { hasFlag } from './terrain';

// Reusable scratch arrays with a generation stamp so we never clear them.
const gScore = new Float64Array(COLS);
const cameFrom = new Int32Array(COLS);
const stamp = new Int32Array(COLS);
const inClosed = new Uint8Array(COLS);
let generation = 0;

// Binary min-heap of [fScore, col] pairs packed into parallel arrays.
const heapF = new Float64Array(COLS + 1);
const heapCol = new Int32Array(COLS + 1);
let heapSize = 0;

function heapPush(f: number, col: number): void {
  heapSize += 1;
  let index = heapSize;
  heapF[index] = f;
  heapCol[index] = col;
  while (index > 1) {
    const parent = index >> 1;
    if ((heapF[parent] ?? Infinity) <= f) break;
    heapF[index] = heapF[parent]!;
    heapCol[index] = heapCol[parent]!;
    index = parent;
  }
  heapF[index] = f;
  heapCol[index] = col;
}

function heapPop(): number {
  const top = heapCol[1]!;
  const lastF = heapF[heapSize]!;
  const lastCol = heapCol[heapSize]!;
  heapSize -= 1;
  if (heapSize > 0) {
    let index = 1;
    for (;;) {
      let child = index << 1;
      if (child > heapSize) break;
      if (child + 1 <= heapSize && (heapF[child + 1] ?? Infinity) < (heapF[child] ?? Infinity)) child += 1;
      if ((heapF[child] ?? Infinity) >= lastF) break;
      heapF[index] = heapF[child]!;
      heapCol[index] = heapCol[child]!;
      index = child;
    }
    heapF[index] = lastF;
    heapCol[index] = lastCol;
  }
  return top;
}

const NEIGHBOR_DELTAS = [1, -1, MAP_W, -MAP_W] as const;

function walkableForPath(terrain: Terrain, col: number): boolean {
  return !hasFlag(terrain, col, FLAG_BUILDING);
}

/**
 * A* over columns. Edges connect 4-neighbors within one level of each other;
 * slopes cost more, roads cost less. Targets may be a single column or a set.
 * Returns the column path excluding the start, or null. `maxNodes` caps heap
 * pops for cosmetic searches so an unreachable target can't flood the map.
 */
export function findPath(
  terrain: Terrain,
  startCol: number,
  targets: number | ReadonlySet<number>,
  maxNodes = Infinity,
): number[] | null {
  const targetSet = typeof targets === 'number' ? null : targets;
  const singleTarget = typeof targets === 'number' ? targets : -1;
  if (targetSet && targetSet.size === 0) return null;

  const isTarget = (col: number): boolean => (targetSet ? targetSet.has(col) : col === singleTarget);
  if (isTarget(startCol)) return [];

  const heuristic = (col: number): number => {
    const x = xOf(col);
    const y = yOf(col);
    if (!targetSet) {
      return (Math.abs(x - xOf(singleTarget)) + Math.abs(y - yOf(singleTarget))) * BALANCE.roadPathCost;
    }
    let best = Infinity;
    for (const target of targetSet) {
      const d = Math.abs(x - xOf(target)) + Math.abs(y - yOf(target));
      if (d < best) best = d;
    }
    return best * BALANCE.roadPathCost;
  };

  generation += 1;
  heapSize = 0;
  stamp[startCol] = generation;
  gScore[startCol] = 0;
  cameFrom[startCol] = -1;
  inClosed[startCol] = 0;
  heapPush(heuristic(startCol), startCol);

  let popped = 0;
  while (heapSize > 0) {
    if (popped >= maxNodes) return null;
    popped += 1;
    const current = heapPop();
    if (stamp[current] === generation && inClosed[current] === 1) continue;
    inClosed[current] = 1;
    if (isTarget(current)) {
      const path: number[] = [];
      let walk = current;
      while (walk !== startCol && walk !== -1) {
        path.push(walk);
        walk = stamp[walk] === generation ? cameFrom[walk]! : -1;
      }
      path.reverse();
      return path;
    }
    const currentDepth = terrain.depth[current] ?? 0;
    const currentG = gScore[current]!;
    const cx = xOf(current);
    for (let index = 0; index < 4; index += 1) {
      const delta = NEIGHBOR_DELTAS[index]!;
      const neighbor = current + delta;
      if (neighbor < 0 || neighbor >= COLS) continue;
      // Prevent horizontal wrap across map rows.
      if (delta === 1 && cx === MAP_W - 1) continue;
      if (delta === -1 && cx === 0) continue;
      if (!walkableForPath(terrain, neighbor)) continue;
      const neighborDepth = terrain.depth[neighbor] ?? 0;
      const depthDelta = Math.abs(neighborDepth - currentDepth);
      let cost: number;
      if (depthDelta > 1) cost = depthDelta * BALANCE.climbPathCostPerLevel;
      else if (depthDelta === 1) cost = BALANCE.slopePathCost;
      else if (hasFlag(terrain, current, FLAG_ROAD) && hasFlag(terrain, neighbor, FLAG_ROAD)) {
        cost = BALANCE.roadPathCost;
      } else cost = 1;
      const tentative = currentG + cost;
      if (stamp[neighbor] !== generation || tentative < (gScore[neighbor] ?? Infinity)) {
        stamp[neighbor] = generation;
        inClosed[neighbor] = 0;
        gScore[neighbor] = tentative;
        cameFrom[neighbor] = current;
        heapPush(tentative + heuristic(neighbor), neighbor);
      }
    }
  }
  return null;
}

export type RouteKind = 'road' | 'pipe' | 'cable' | 'paved';

const ROUTE_FLAGS: Record<RouteKind, number> = {
  road: FLAG_ROAD,
  pipe: FLAG_PIPE,
  cable: FLAG_CABLE,
  paved: FLAG_PAVED,
};

/** Riding a tile that already carries the flag is nearly free, so previews hug
 * existing runs — which also cost nothing to re-commit. */
const ROUTE_REUSE_COST = 0.25;

/**
 * A* for route-tool drag previews. Shares the scratch arrays with findPath —
 * callers are single-threaded and never interleave searches. Pipes and cables
 * may run under buildings and are depth-blind (the flood fills are too);
 * roads detour around buildings and refuse cliffs; paving only follows roads.
 * Returns the column path excluding the start, or null.
 */
export function findRoutePath(
  terrain: Terrain,
  startCol: number,
  targetCol: number,
  kind: RouteKind,
  maxNodes = COLS,
): number[] | null {
  if (targetCol === startCol) return [];
  const ownFlag = ROUTE_FLAGS[kind];
  const passable = (col: number): boolean => {
    if (kind === 'road') return !hasFlag(terrain, col, FLAG_BUILDING);
    if (kind === 'paved') return hasFlag(terrain, col, FLAG_ROAD);
    return true;
  };
  if (!passable(targetCol)) return null;

  const tx = xOf(targetCol);
  const ty = yOf(targetCol);
  const heuristic = (col: number): number =>
    (Math.abs(xOf(col) - tx) + Math.abs(yOf(col) - ty)) * ROUTE_REUSE_COST;

  generation += 1;
  heapSize = 0;
  stamp[startCol] = generation;
  gScore[startCol] = 0;
  cameFrom[startCol] = -1;
  inClosed[startCol] = 0;
  heapPush(heuristic(startCol), startCol);

  let popped = 0;
  while (heapSize > 0) {
    if (popped >= maxNodes) return null;
    popped += 1;
    const current = heapPop();
    if (stamp[current] === generation && inClosed[current] === 1) continue;
    inClosed[current] = 1;
    if (current === targetCol) {
      const path: number[] = [];
      let walk = current;
      while (walk !== startCol && walk !== -1) {
        path.push(walk);
        walk = stamp[walk] === generation ? cameFrom[walk]! : -1;
      }
      path.reverse();
      return path;
    }
    const currentDepth = terrain.depth[current] ?? 0;
    const currentG = gScore[current]!;
    const cx = xOf(current);
    for (let index = 0; index < 4; index += 1) {
      const delta = NEIGHBOR_DELTAS[index]!;
      const neighbor = current + delta;
      if (neighbor < 0 || neighbor >= COLS) continue;
      if (delta === 1 && cx === MAP_W - 1) continue;
      if (delta === -1 && cx === 0) continue;
      if (!passable(neighbor)) continue;
      const depthDelta = Math.abs((terrain.depth[neighbor] ?? 0) - currentDepth);
      if (kind === 'road' && depthDelta > 1) continue;
      let cost = hasFlag(terrain, neighbor, ownFlag) ? ROUTE_REUSE_COST : 1;
      if (kind === 'road' && depthDelta === 1) cost += 0.5;
      const tentative = currentG + cost;
      if (stamp[neighbor] !== generation || tentative < (gScore[neighbor] ?? Infinity)) {
        stamp[neighbor] = generation;
        inClosed[neighbor] = 0;
        gScore[neighbor] = tentative;
        cameFrom[neighbor] = current;
        heapPush(tentative + heuristic(neighbor), neighbor);
      }
    }
  }
  return null;
}

/** Walkable tiles adjacent to a building footprint — used as path targets. */
export function accessTiles(terrain: Terrain, x: number, y: number, w: number, h: number): Set<number> {
  const result = new Set<number>();
  const tryAdd = (tx: number, ty: number): void => {
    if (!inBounds(tx, ty)) return;
    const col = colOf(tx, ty);
    if (!hasFlag(terrain, col, FLAG_BUILDING)) result.add(col);
  };
  for (let dx = 0; dx < w; dx += 1) {
    tryAdd(x + dx, y - 1);
    tryAdd(x + dx, y + h);
  }
  for (let dy = 0; dy < h; dy += 1) {
    tryAdd(x - 1, y + dy);
    tryAdd(x + w, y + dy);
  }
  return result;
}
